import React, { Fragment, useState } from "react";

// Material-UI Components
import Drawer from "@material-ui/core/Drawer";

// Icons
import closeIcon from "../../../assets/icons/close.svg";
import yellowMailingIcon from "../../../assets/icons/templates_icons/yellow-note-text.svg";

// Custom Components
import RequestColorDropdown from "../Dropdown/RequestColorDropdown";
import SplitFileUploadButton from "../buttons/SplitFileUploadButton/SplitFileUploadButton";

// Stylesheets
import "./TemplateDrawer.scss";

// Custom Hooks
import {
  ADD_DOCUMENT,
  REMOVE_DOCUMENT,
  RESET_TEMPLATE_FORM_STATE,
  UPDATE_COLOR_CODE,
  UPDATE_DOCUMENT,
  UPDATE_TEMPLATE_TITLE,
  getEmptyRequiredDocuments,
} from "./hooks/useTemplateForm";

// Apollo Client
import { gql, useMutation } from "@apollo/client";

// Constants
import { colorCodes } from "../constant/common";

// External Utilities
import { uploadClient } from "../../..";
import { GET_ALL_DOCUMENT_REQUEST_TEMPLATES } from "../LeftSection/LeftSection";

// Internal Utilities
import { convertToString } from "../../../helper";
import { notify } from "../../Notify/Notify";
import ConfirmationPopup from "../../../common/components/Popup/ConfirmationPopup";

// Graphql related
const CREATE_DOCUMENT_REQUEST_TEMPLATE = gql`
  mutation CreateDocumentRequestTemplate(
    $files: [Upload!]!
    $filesInfo: String!
    $title: String!
    $color: String!
    $documentsWithoutFile: [String]
  ) {
    createDocumentRequestTemplate(
      files: $files
      filesInfo: $filesInfo
      title: $title
      color: $color
      documentsWithoutFile: $documentsWithoutFile
    ) {
      createdTemplate {
        id
        title
        color
        templateDocuments {
          id
          document
          documentName
        }
      }
    }
  }
`;

const UPDATE_DOCUMENT_REQUEST_TEMPLATE = gql`
  mutation UpdateDocumentRequestTemplate(
    $id: String!
    $title: String!
    $color: String!
    $newFiles: [Upload]
    $newFilesInfo: String
    $removedDocuments: [String]
    $updatedFiles: [Upload]
    $updatedFilesInfo: [UpdatedRequestDocumentsInputType]
    $documentsWithoutFile: [String]
  ) {
    updateDocumentRequestTemplate(
      id: $id
      title: $title
      color: $color
      newFiles: $newFiles
      newFilesInfo: $newFilesInfo
      removedDocuments: $removedDocuments
      updatedFiles: $updatedFiles
      updatedFilesInfo: $updatedFilesInfo
      documentsWithoutFile: $documentsWithoutFile
    ) {
      updatedTemplate {
        id
      }
    }
  }
`;
const DELETE_DOCUMENT_REQUEST_TEMPLATE = gql`
  mutation deleteDocumentRequestTemplate($id: String!) {
    deleteDocumentRequestTemplate(id: $id) {
      success
    }
  }
`;

function TemplateDrawer({
  isOpen = false,
  onClose = () => console.log("Onclose is missing for TemplateDrawer"),
  isEdit = false,
  templateState = {},
  onChange = () => console.log("OnChange is not provided to MailingListDrawer"),
}) {
  // Mutations
  const [createTemplate, { loading: isCreateTemplateMutationLoading }] =
    useMutation(CREATE_DOCUMENT_REQUEST_TEMPLATE, {
      client: uploadClient,
      refetchQueries: [{ query: GET_ALL_DOCUMENT_REQUEST_TEMPLATES }],
      onError: (error) => {
        const title = "Error";
        const detail = "Something went wrong.";
        const type = "error";
        notify(title, detail, type);
        console.error("Create Template Mutation Error:", error);
      },
      onCompleted: () => {
        const title = "Success";
        const detail = `Template ${convertToString(
          templateState.templateTitle
        )} is successfully created.`;
        const type = "success";
        notify(title, detail, type);
        onClose(isEdit);
      },
      awaitRefetchQueries: true,
    });

  const [updateTemplate, { loading: isUpdateTemplateMutationLoading }] =
    useMutation(UPDATE_DOCUMENT_REQUEST_TEMPLATE, {
      client: uploadClient,
      refetchQueries: [{ query: GET_ALL_DOCUMENT_REQUEST_TEMPLATES }],
      onError: (error) => {
        const title = "Error";
        const detail = "Something went wrong.";
        const type = "error";
        notify(title, detail, type);
        console.error("Update Template Mutation Error:", error);
      },
      onCompleted: () => {
        const title = "Success";
        const detail = `Template ${convertToString(
          templateState.templateTitle
        )} is successfully updated.`;
        const type = "success";
        notify(title, detail, type);
        onClose(isEdit);
      },
      awaitRefetchQueries: true,
    });

  const [deleteTemplate, { loading: isDeleteLoading }] = useMutation(
    DELETE_DOCUMENT_REQUEST_TEMPLATE,
    {
      client: uploadClient,
      refetchQueries: [{ query: GET_ALL_DOCUMENT_REQUEST_TEMPLATES }],
      onError: (error) => {
        const title = "Error";
        const detail = "Something went wrong.";
        const type = "error";
        notify(title, detail, type);
        console.error("Delete Template Mutation Error:", error);
      },
      onCompleted: (data) => {
        const title = "Success";
        const detail = `Template ${convertToString(
          templateState.templateTitle
        )} is successfully deleted.`;
        const type = "success";
        notify(title, detail, type);
        onClose(isEdit);
      },
      awaitRefetchQueries: true,
    }
  );

  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] =
    useState(false);

  const isSubmitBtnDisable = () => {
    if (
      !isValidTemplateState(templateState) ||
      isCreateTemplateMutationLoading
    ) {
      return true;
    }

    return false;
  };
  const isSaveChangesBtnDisabled =
    (isEdit &&
      Object.keys(
        getChangesPayload(templateState.initialStateForEdit, templateState)
      ).length === 0) ||
    isUpdateTemplateMutationLoading;

  return (
    <Fragment>
      <Drawer open={isOpen}>
        <div className="new-design-v1 template-drawer-container-wrapper">
          <main className="template-drawer-container ">
            <section className="template-drawer-header">
              <div className="template-drawer-header-label">
                {`${isEdit ? "Edit" : "Create"} Template`}
              </div>
              <img
                className="close"
                src={closeIcon}
                alt="closeIcon"
                onClick={() => {
                  onClose(isEdit);
                  onChange(RESET_TEMPLATE_FORM_STATE);
                }}
                width={"24px"}
                height={"24px"}
              />
            </section>
            <section
              className="template-drawer-form pb-8"
              style={{ maxHeight: "100%", overflowY: "auto" }}
            >
              <div className="mb-5">
                <label
                  htmlFor="templateTitle"
                  className="mb-3 template-drawer-form-label"
                >
                  Template Title *
                </label>
                <input
                  type="text"
                  placeholder="Enter folder name"
                  maxLength="50"
                  id="templateTitle"
                  className="min-height-49"
                  value={templateState.templateTitle}
                  onChange={(e) =>
                    onChange(UPDATE_TEMPLATE_TITLE, e.target.value)
                  }
                />
              </div>
              <div className="mb-8">
                <label className="mb-3 template-drawer-form-label">
                  Color Code *
                </label>
                <RequestColorDropdown
                  templateColors={colorCodes}
                  onChange={(value) => {
                    onChange(UPDATE_COLOR_CODE, value);
                  }}
                  currentSelectedValue={templateState.colorCode}
                />
              </div>
              <div className="recipients-list-wrapper">
                <div className="recipients-list">
                  <img
                    src={yellowMailingIcon}
                    width={"40px"}
                    height={"40px"}
                    alt="mailing"
                  />
                  <div className="recipients-list-header">
                    {"Requested Documents"}
                  </div>
                </div>
                <div className="recipients-list-sub-header pb-8">
                  {"Use this area to indicate the required documents."}
                </div>
                <div className="required-docs-container">
                  {Array.isArray(templateState.requiredDocuments) &&
                    templateState.requiredDocuments.map((requiredDocument) => {
                      return (
                        <RequiredDocumentCard
                          key={requiredDocument.documentId}
                          requiredDocument={requiredDocument}
                          onChange={onChange}
                        />
                      );
                    })}
                  <button
                    className="light-primary-button mt-5"
                    onClick={() =>
                      onChange(ADD_DOCUMENT, { ...getEmptyRequiredDocuments() })
                    }
                  >
                    Add Another
                  </button>
                </div>
              </div>
            </section>
            {/*  Footer  */}
            <section className="px-8">
              {!isEdit && (
                <button
                  className="primary-button"
                  onClick={async () => {
                    const payload = transformStateToPayload(templateState);
                    await createTemplate({ variables: { ...payload } });
                  }}
                  disabled={isSubmitBtnDisable()}
                >
                  Create Template
                </button>
              )}
              {isEdit && (
                <div className="edit-footer-container">
                  <button
                    className="secondary-button"
                    onClick={() => {
                      setIsDeleteConfirmationPopupOpen(true);
                    }}
                  >
                    Delete
                  </button>
                  <button
                    className="primary-button"
                    onClick={async () => {
                      if (templateState.requiredDocuments?.length === 0) {
                        const title = "Error";
                        const detail = "Please add at least one document.";
                        const type = "error";
                        notify(title, detail, type);
                        return;
                      }
                      const payload = getChangesPayload(
                        templateState.initialStateForEdit,
                        templateState
                      );
                      await updateTemplate({
                        variables: { ...payload },
                      });
                    }}
                    disabled={isSaveChangesBtnDisabled}
                  >
                    Save Changes
                  </button>
                </div>
              )}
            </section>
          </main>
        </div>
      </Drawer>
      <ConfirmationPopup
        title="Delete Template"
        description={`Are you sure want to delete ${templateState.templateTitle} ?`}
        open={isDeleteConfirmationPopupOpen}
        confirmationOnClick={async () => {
          await deleteTemplate({
            variables: {
              id: templateState.templateId,
            },
          });
          onClose(isEdit);
          setIsDeleteConfirmationPopupOpen(false);
        }}
        isConfirmationBtnDisable={isDeleteLoading}
        onClose={() => {
          setIsDeleteConfirmationPopupOpen(false);
        }}
      />
    </Fragment>
  );
}

export default TemplateDrawer;

// Helper components

function RequiredDocumentCard({ requiredDocument, onChange }) {
  return (
    <div className="required-docs-card ">
      <label htmlFor="templateTitle" className="template-drawer-form-label">
        Document
      </label>
      <div className="docs-name-input-container">
        <input
          type="text"
          placeholder="Enter Document Name"
          maxLength="50"
          id="templateTitle"
          className="min-height-49"
          value={requiredDocument.documentName}
          onChange={(e) =>
            onChange(UPDATE_DOCUMENT, {
              documentId: requiredDocument.documentId,
              documentName: e.target.value,
            })
          }
        />

        <img
          className="close"
          src={closeIcon}
          alt="closeIcon"
          onClick={() => onChange(REMOVE_DOCUMENT, requiredDocument.documentId)}
          width={"20px"}
          height={"20px"}
        />
      </div>
      <SplitFileUploadButton
        fileName={requiredDocument.documentFileName}
        onChange={(documentFile) => {
          const documentFileName =
            documentFile !== null ? documentFile.name : "";
          onChange(UPDATE_DOCUMENT, {
            documentId: requiredDocument.documentId,
            documentFileName,
            documentFile,
          });
        }}
        isFileUploadDisabled={requiredDocument.documentFile === -1}
      />
    </div>
  );
}

// Helper functions
/**
 * Transforms the provided state into a payload for API requests.
 *
 * @param {object} state - The state object containing template information.
 * @param {string} state.templateTitle - The title of the template.
 * @param {object} state.colorCode - The color code object for the template.
 * @param {string} state.colorCode.label - The label of the color code.
 * @param {Array} state.requiredDocuments - An array of required documents for the template.
 * @param {object} state.requiredDocuments[].documentFile - The file associated with the document.
 * @param {string} state.requiredDocuments[].documentFileName - The name of the document file.
 * @param {string} state.requiredDocuments[].documentName - The name of the document.
 *
 * @returns {object} A payload for API requests containing template information.
 */
function transformStateToPayload(state) {
  // Extracting data from state
  const { templateTitle, colorCode, requiredDocuments } = state;

  // Transforming colorCode object
  const color = colorCode.label;

  // Creating filesInfo object
  const filesInfo = requiredDocuments
    ?.filter((u) => u.documentFile !== null)
    ?.reduce((acc, doc) => {
      acc[doc.documentFileName] = doc.documentName;
      return acc;
    }, {});

  // Creating files array
  const files = requiredDocuments
    .filter((u) => u.documentFile !== null)
    .map((doc) => doc.documentFile);
  const documentsWithoutFile = requiredDocuments
    .filter((u) => u.documentFile === null)
    .map((doc) => doc.documentName);

  // Constructing the payload
  const payload = {
    title: templateTitle,
    color: color,
    filesInfo: JSON.stringify(filesInfo),
    files: files,
    documentsWithoutFile: documentsWithoutFile,
  };

  return payload;
}

/**
 * Checks whether the provided template state is valid.
 *
 * @param {object} templateState - The state object representing a template.
 * @param {string} templateState.templateTitle - The title of the template.
 * @param {Array} templateState.requiredDocuments - An array of required documents for the template.
 * @param {string} templateState.requiredDocuments[].documentName - The name of the document.
 *
 * @returns {boolean} True if the template state is valid, false otherwise.
 */
function isValidTemplateState(templateState = {}) {
  if (!templateState || typeof templateState !== "object") {
    return false; // Input is not an object
  }
  const { templateTitle, requiredDocuments } = templateState;

  if (
    typeof templateTitle !== "string" ||
    templateTitle.trim() === "" ||
    requiredDocuments.length === 0
  ) {
    return false; // Template title is empty or contains only whitespace, or requiredDocuments array is empty
  }
  // Check if any element in requiredDocuments has documentName empty or containing only whitespace
  if (
    requiredDocuments.some(
      (doc) =>
        typeof doc.documentName !== "string" || doc.documentName.trim() === ""
    )
  ) {
    return false;
  }

  // All conditions passed, the templateState is considered valid
  return true;
}

/**
 * Generates a payload representing the changes between initial and final states of a template.
 *
 * @param {object} initialState - The initial state of the template.
 * @param {string} initialState.templateTitle - The title of the template.
 * @param {object} initialState.colorCode - The color code object for the template.
 * @param {string} initialState.colorCode.label - The label of the color code.
 * @param {string} initialState.templateId - The ID of the template.
 *
 * @param {object} finalState - The final state of the template.
 * @param {string} finalState.templateTitle - The title of the template.
 * @param {object} finalState.colorCode - The color code object for the template.
 * @param {string} finalState.colorCode.label - The label of the color code.
 *
 * @returns {object} A payload representing the changes between the initial and final states.
 */
function getChangesPayload(initialState, finalState) {
  if (Object.keys(initialState).length === 0) {
    return {};
  }
  const payload = {};
  // Check if colorCode.label has changed
  if (initialState?.colorCode?.label !== finalState?.colorCode?.label) {
    payload.color = finalState.colorCode.label;
  }
  if (initialState.templateTitle !== finalState.templateTitle) {
    payload.title = finalState.templateTitle;
  }
  // Check for changes files
  const documentPayload = getDocumentChanges(initialState, finalState);
  if (
    Object.keys(documentPayload).length === 0 &&
    Object.keys(payload).length === 0
  ) {
    return {};
  }
  return {
    id: initialState.templateId,
    title: finalState.templateTitle,
    color: finalState.colorCode.label,
    ...documentPayload,
  };
}

/**
 * Compares initial and final states of required documents and generates a payload
 * representing the changes, including added, edited, and deleted documents.
 *
 * @param {object} initState - The initial state of the template.
 * @param {Array} initState.requiredDocuments - An array of initial required documents for the template.
 *
 * @param {object} finalState - The final state of the template.
 * @param {Array} finalState.requiredDocuments - An array of final required documents for the template.
 *
 * @returns {object} A payload representing the changes in required documents.
 *                   The payload includes information about added, edited, and deleted documents.
 */
function getDocumentChanges(initState, finalState) {
  const payload = {};
  const changes = {
    added: [],
    editedNames: [],
    deleted: [],
    editedDocs: [],
  };

  // Check for added and edited documents
  finalState.requiredDocuments.forEach((finalDoc) => {
    const matchingDoc = initState.requiredDocuments.find(
      (initDoc) => initDoc.documentId === finalDoc.documentId
    );

    if (!matchingDoc) {
      changes.added.push(finalDoc);
    } else {
      const isNameChanged = matchingDoc.documentName !== finalDoc.documentName;
      const isFileNameChanged =
        matchingDoc.documentFileName !== finalDoc.documentFileName;
      if (isNameChanged && isFileNameChanged) {
        changes.added.push(finalDoc);
        changes.deleted.push(finalDoc);
        return;
      }
      if (
        !(isNameChanged && isFileNameChanged) &&
        (isNameChanged || isFileNameChanged)
      ) {
        const editedDoc = {
          documentId: finalDoc.documentId,
          documentFile:
            finalDoc.documentFile || matchingDoc.documentFile || null,
        };

        if (isNameChanged) {
          editedDoc.documentName = finalDoc.documentName;
          changes.editedNames.push(editedDoc);
          return;
        }

        if (isFileNameChanged) {
          editedDoc.documentFileName = finalDoc.documentFileName;
          changes.editedDocs.push(editedDoc);
        }
      }
    }
  });

  // Check for deleted documents
  initState.requiredDocuments.forEach((initDoc) => {
    const matchingDoc = finalState.requiredDocuments.find(
      (finalDoc) => initDoc.documentId === finalDoc.documentId
    );

    if (!matchingDoc) {
      changes.deleted.push(initDoc);
    }
  });

  if (changes.added.length > 0) {
    payload["newFiles"] = changes.added
      .filter((u) => u.documentFile !== null)
      .map((doc) => doc.documentFile);
    const filesInfo = changes.added
      .filter((u) => u.documentFile !== null)
      .reduce((acc, doc) => {
        acc[doc.documentFileName] = doc.documentName;
        return acc;
      }, {});
    const documentsWithoutFile = changes.added
      .filter((u) => u.documentFile === null)
      .map((doc) => doc.documentName);
    payload["documentsWithoutFile"] = documentsWithoutFile;
    payload["newFilesInfo"] = JSON.stringify(filesInfo);
  }
  if (changes.deleted.length > 0) {
    payload["removedDocuments"] = changes.deleted.map(
      (element) => element.documentId
    );
  }
  if (changes.editedDocs.length > 0) {
    payload["updatedFiles"] = changes.editedDocs.map((doc) => doc.documentFile);
    payload["updatedFilesInfo"] = changes.editedDocs.map((doc) => ({
      documentId: doc.documentId,
      newFileUpload: doc.documentFileName,
    }));
  }
  if (changes.editedNames.length > 0) {
    if ("updatedFilesInfo" in payload) {
      payload["updatedFilesInfo"] = [
        ...payload["updatedFilesInfo"],
        ...changes.editedNames.map((doc) => ({
          documentId: doc.documentId,
          newDocumentName: doc.documentName,
        })),
      ];
    } else {
      payload["updatedFilesInfo"] = changes.editedNames.map((doc) => ({
        documentId: doc.documentId,
        newDocumentName: doc.documentName,
      }));
    }
  }
  return payload;
}
